import { motion } from "framer-motion";
import { Search, Compass, PenTool, Hammer, Rocket } from "lucide-react";

const STEPS = [
  {
    icon: Search,
    label: "Discover",
    description:
      "We map your stack, your buyers, your partners and your constraints. No assumptions — only what is real.",
    testid: "process-step-discover",
  },
  {
    icon: Compass,
    label: "Strategy",
    description:
      "We define scope, architecture, partner network and the path to measurable outcomes.",
    testid: "process-step-strategy",
  },
  {
    icon: PenTool,
    label: "Design",
    description:
      "Strong, accessible interfaces and contracts that reflect how B2B users actually buy and operate.",
    testid: "process-step-design",
  },
  {
    icon: Hammer,
    label: "Develop",
    description:
      "Our senior team ships in tight increments with reviews, observability and quality gates.",
    testid: "process-step-develop",
  },
  {
    icon: Rocket,
    label: "Deploy & Scale",
    description:
      "We launch, harden and stay on — monitoring, iterating and growing the platform and partner network with you.",
    testid: "process-step-deploy-scale",
  },
];

const Process = () => {
  return (
    <section id="process" data-testid="process-section" className="section-pad relative border-t border-[rgba(244,234,222,0.08)]">
      <div className="container-narrow">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.45 }}
          className="max-w-3xl"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#E94B4B]">
            — How we deliver
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F4EADE]">
            A disciplined process. Built for outcomes.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#EFE3D6]/75 leading-relaxed">
            Five steps. One accountable team. Predictable delivery from kickoff to scale.
          </p>
        </motion.div>

        <div className="mt-14 relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-[rgba(244,234,222,0.10)]" aria-hidden="true" />

          <div className="space-y-8 md:space-y-12">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isRight = i % 2 === 1;
              return (
                <motion.div
                  key={s.label}
                  data-testid={s.testid}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="relative md:grid md:grid-cols-2 md:gap-12 items-center"
                >
                  <div className={`${isRight ? "md:order-2" : ""}`}>
                    <div className="relative rounded-2xl border border-[rgba(244,234,222,0.12)] bg-[rgba(17,16,21,0.7)] p-6 sm:p-7 md:max-w-md md:ml-auto md:mr-0" style={isRight ? { marginLeft: 0, marginRight: "auto" } : {}}>
                      <div className="flex items-center gap-3">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#6B1F47]/30 ring-1 ring-[#6B1F47]/50 text-[#F4EADE]">
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">
                          Step 0{i + 1}
                        </span>
                      </div>
                      <h3 className="mt-4 font-display text-2xl font-semibold text-[#F4EADE]">
                        {s.label}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-[#EFE3D6]/80">
                        {s.description}
                      </p>
                    </div>
                  </div>

                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center justify-center">
                    <div className="h-3 w-3 rounded-full bg-[#E94B4B] ring-4 ring-[#0B0A0C]" />
                  </div>

                  <div className={`${isRight ? "md:order-1" : ""} hidden md:block`} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
